import React from "react";
import styled from "styled-components";
import { primaryDarkColor } from "../styles/color";

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  modalContainerWidth,
  /** e.g. `96vh` — taller sheet for dense forms */
  modalMaxHeight,
  /** `"visible"` shows full content without an inner scrollbar (overlay scrolls if needed) */
  bodyOverflow,
  compactHeader,
  /** White header without grey bar — cleaner dialogs */
  plainHeader,
  /** Inner body padding, e.g. `"24px 28px"` */
  bodyPadding,
}) => {
  if (!isOpen) return null;

  const maxH = modalMaxHeight ?? "min(92vh, 100%)";
  const bodyOv = bodyOverflow ?? "auto";
  const containerOverflow =
    bodyOv === "visible" && maxH === "none" ? "visible" : "hidden";

  return (
    <Overlay onClick={onClose}>
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        width={modalContainerWidth}
        $maxHeight={maxH}
        $containerOverflow={containerOverflow}
      >
        <ModalHeader $compact={compactHeader} $plain={plainHeader}>
          <h2>{title}</h2>
          <ModalClose onClick={onClose}>&times;</ModalClose>
        </ModalHeader>
        <ModalBody
          $overflowY={bodyOv}
          $scrollInside={bodyOv !== "visible"}
          $padding={bodyPadding}
        >
          {children}
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

export default Modal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 99999;
  padding: 24px 16px 32px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const ModalContainer = styled.div`
  background: #fff;
  padding: 0;
  border-radius: 12px;
  width: ${props => props.width || '50%'} !important;
  min-width: 400px;
  max-width: ${props => props.width || '50%'} !important;
  max-height: ${(p) => p.$maxHeight};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: ${(p) => p.$containerOverflow};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  animation: modalFadeIn 0.2s ease-out;
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @media (max-width: 768px) {
    width: 95% !important;
    max-width: 95% !important;
    min-width: unset;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  border-bottom: ${(p) => (p.$plain ? "none" : "1px solid #e0e0e0")};
  padding: ${(p) => {
    if (p.$plain) return "24px 28px 0";
    if (p.$compact) return "14px 18px 12px 18px";
    return "20px 20px 15px 20px";
  }};
  margin-bottom: 0;
  color: ${primaryDarkColor};
  background: ${(p) => (p.$plain ? "#ffffff" : "#f8f9fa")};
  
  h2 {
    margin: 0;
    font-size: ${(p) => (p.$compact ? "18px" : p.$plain ? "22px" : "20px")};
    font-weight: 600;
    letter-spacing: -0.01em;
  }
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e0e0e0;
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: ${(p) => p.$padding || "0"};
  overflow-x: hidden;
  overflow-y: ${(p) => {
    if (p.$overflowY === "visible") return "visible";
    if (p.$scrollInside) return "auto";
    return p.$overflowY || "auto";
  }};
  flex: ${(p) => (p.$scrollInside || p.$overflowY !== "visible" ? "1" : "0 1 auto")};
  min-height: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
`;
